import React, { useEffect, useMemo, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { RESOURCES } from "@/config/resources";
import { useI18n } from "@/i18n";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Plus, Pencil, Trash2, Download, Search } from "lucide-react";
import { toast } from "sonner";
import { motion } from "framer-motion";

const STATUS_COLORS = {
  active: "bg-emerald-100 text-emerald-700", confirmed: "bg-emerald-100 text-emerald-700",
  paid: "bg-emerald-100 text-emerald-700", present: "bg-emerald-100 text-emerald-700",
  inactive: "bg-slate-100 text-slate-600", cancelled: "bg-slate-100 text-slate-600",
  closed: "bg-slate-100 text-slate-600",
  unpaid: "bg-red-100 text-red-700", absent: "bg-red-100 text-red-700",
  partial: "bg-amber-100 text-amber-700", late: "bg-amber-100 text-amber-700",
  pending: "bg-amber-100 text-amber-700", open: "bg-sky-100 text-sky-700",
  in_progress: "bg-sky-100 text-sky-700",
};

export default function ResourcePage({ resourceKey }) {
  const cfg = RESOURCES[resourceKey];
  const { t } = useI18n();
  const { hasRole } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sources, setSources] = useState({});
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [query, setQuery] = useState("");

  const canWrite = !cfg.readonly && cfg.write && cfg.write.length > 0 && hasRole(...cfg.write);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(cfg.endpoint);
      setRows(data);
    } catch (e) {
      toast.error(apiErr(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // load option sources only for users who can create/edit (portal roles skip this to avoid 403s)
    if (cfg.write && cfg.write.length > 0 && hasRole(...cfg.write)) {
      const srcKeys = [...new Set((cfg.fields || []).filter((f) => f.source).map((f) => f.source))];
      srcKeys.forEach(async (sk) => {
        try {
          const { data } = await api.get(RESOURCES[sk].endpoint);
          setSources((prev) => ({ ...prev, [sk]: data }));
        } catch (e) {}
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resourceKey]);

  const openCreate = () => { setEditing(null); setForm({}); setOpen(true); };
  const openEdit = (row) => { setEditing(row); setForm({ ...row }); setOpen(true); };

  const submit = async () => {
    try {
      if (editing) await api.put(`${cfg.endpoint}/${editing.id}`, form);
      else await api.post(cfg.endpoint, form);
      toast.success(t("save"));
      setOpen(false);
      load();
    } catch (e) {
      toast.error(apiErr(e.response?.data?.detail));
    }
  };

  const remove = async (row) => {
    if (!window.confirm(`${t("delete")} ?`)) return;
    try {
      await api.delete(`${cfg.endpoint}/${row.id}`);
      toast.success(t("delete"));
      load();
    } catch (e) {
      toast.error(apiErr(e.response?.data?.detail));
    }
  };

  const downloadPdf = (row) => {
    window.open(`${process.env.REACT_APP_BACKEND_URL}/api${cfg.pdf}/${row.id}/pdf`, "_blank");
  };

  const filtered = useMemo(() => {
    if (!query) return rows;
    const q = query.toLowerCase();
    return rows.filter((r) => JSON.stringify(r).toLowerCase().includes(q));
  }, [rows, query]);

  const renderCell = (row, col) => {
    let v = row[col.key];
    if (v == null || v === "") return <span className="text-slate-400">—</span>;
    if (col.key.includes("date") || col.key === "created_at") v = String(v).slice(0, 10);
    if (col.badge) {
      return <Badge className={`${STATUS_COLORS[v] || "bg-slate-100 text-slate-600"} border-0 font-medium`}>{t(v) !== v ? t(v) : v}</Badge>;
    }
    if (["amount", "total"].includes(col.key)) return <span className="tabular-nums">{Number(v).toLocaleString()} </span>;
    return String(v);
  };

  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.25 }}>
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-display font-bold text-slate-900">{t(cfg.titleKey)}</h1>
          <p className="text-sm text-slate-500">{filtered.length} {t(cfg.titleKey).toLowerCase()}</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input data-testid="resource-search" value={query} onChange={(e) => setQuery(e.target.value)}
              placeholder={t("search")} className="pl-9 w-56 bg-white" />
          </div>
          {canWrite && (
            <Button data-testid="resource-add-btn" onClick={openCreate} className="rounded-full gap-2">
              <Plus className="h-4 w-4" /> {t("add")}
            </Button>
          )}
        </div>
      </div>

      <Card className="card-float border-slate-200 overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50/70 hover:bg-slate-50/70">
              {cfg.columns.map((c) => (
                <TableHead key={c.key} className="font-semibold text-slate-600">{t(c.labelKey)}</TableHead>
              ))}
              {(canWrite || cfg.pdf) && <TableHead className="text-right">{t("actions")}</TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={cfg.columns.length + 1} className="text-center py-10 text-slate-400">{t("loading")}</TableCell></TableRow>
            ) : filtered.length === 0 ? (
              <TableRow><TableCell colSpan={cfg.columns.length + 1} className="text-center py-10 text-slate-400">{t("noData")}</TableCell></TableRow>
            ) : filtered.map((row, i) => (
              <TableRow key={row.id} data-testid={`row-${resourceKey}`} className={i % 2 ? "bg-slate-50/40" : ""}>
                {cfg.columns.map((c) => (
                  <TableCell key={c.key} className="text-slate-700">{renderCell(row, c)}</TableCell>
                ))}
                {(canWrite || cfg.pdf) && (
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      {cfg.pdf && (
                        <Button data-testid="pdf-btn" variant="ghost" size="icon" onClick={() => downloadPdf(row)} title={t("pdf")}>
                          <Download className="h-4 w-4 text-primary" />
                        </Button>
                      )}
                      {canWrite && (
                        <>
                          <Button data-testid="edit-btn" variant="ghost" size="icon" onClick={() => openEdit(row)}>
                            <Pencil className="h-4 w-4 text-slate-500" />
                          </Button>
                          <Button data-testid="delete-btn" variant="ghost" size="icon" onClick={() => remove(row)}>
                            <Trash2 className="h-4 w-4 text-red-500" />
                          </Button>
                        </>
                      )}
                    </div>
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-display">{editing ? t("edit") : t("add")} — {t(cfg.titleKey)}</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-2 max-h-[60vh] overflow-y-auto pr-1">
            {(cfg.fields || []).map((f) => (
              <div key={f.name} className="grid gap-1.5">
                <label className="text-sm font-medium text-slate-600">{t(f.labelKey)}</label>
                {f.type === "textarea" ? (
                  <Textarea data-testid={`field-${f.name}`} value={form[f.name] || ""}
                    onChange={(e) => setForm({ ...form, [f.name]: e.target.value })} />
                ) : f.type === "select" ? (
                  <Select value={form[f.name] || ""} onValueChange={(v) => setForm({ ...form, [f.name]: v })}>
                    <SelectTrigger data-testid={`field-${f.name}`}><SelectValue placeholder="—" /></SelectTrigger>
                    <SelectContent>
                      {(sources[f.source] || []).map((o) => (
                        <SelectItem key={o.id} value={o.id}>{o[f.optionLabel] || o.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : f.type === "enum" ? (
                  <Select value={form[f.name] || ""} onValueChange={(v) => setForm({ ...form, [f.name]: v })}>
                    <SelectTrigger data-testid={`field-${f.name}`}><SelectValue placeholder="—" /></SelectTrigger>
                    <SelectContent>
                      {f.options.map((o) => <SelectItem key={o} value={o}>{t(o) !== o ? t(o) : o}</SelectItem>)}
                    </SelectContent>
                  </Select>
                ) : (
                  <Input data-testid={`field-${f.name}`} type={f.type === "number" ? "number" : f.type === "date" ? "date" : "text"}
                    value={form[f.name] || ""} onChange={(e) => setForm({ ...form, [f.name]: f.type === "number" ? Number(e.target.value) : e.target.value })} />
                )}
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>{t("cancel")}</Button>
            <Button data-testid="resource-save-btn" onClick={submit}>{t("save")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </motion.div>
  );
}
